import useWorldStore from '../store/worldStore';
import { getBackendErrorDetail } from '../../../lib/backend/error-details.js';
import { postWorldTaskEvent } from '../../../lib/backend/world';
import { saveMemory } from './agentMemory';
import { runAgentTask } from './openclawChat';
import { findTaskByIdentity } from '../lib/taskIdentity';
import {
  buildOpenClawTaskEndPayload,
  buildOpenClawTaskStartPayload,
  buildOpenClawTaskToolUse,
  findRecentPlatformSyncedCompletion,
  findTaskInSnapshot,
} from '../lib/openclawTaskSync.mjs';

async function syncOpenClawTaskStart(agentId, task, toolUse) {
  if (!task?.id) return null;

  try {
    const snapshot = await postWorldTaskEvent(
      agentId,
      buildOpenClawTaskStartPayload(task, toolUse)
    );
    useWorldStore.getState().applyAgentTaskSnapshot(agentId, snapshot);
    return snapshot;
  } catch (error) {
    console.warn('[task-executor] failed to sync task start', agentId, error);
    return null;
  }
}

async function syncOpenClawTaskEnd(agentId, payload) {
  try {
    const snapshot = await postWorldTaskEvent(agentId, payload);
    useWorldStore.getState().applyAgentTaskSnapshot(agentId, snapshot);
    return snapshot;
  } catch (error) {
    console.warn('[task-executor] failed to sync task end', agentId, error);
    return null;
  }
}

function getTaskAgentIds(agentId, agent, activeTask) {
  return Array.from(new Set([
    agentId,
    activeTask?.agentId,
    agent?.id,
    agent?.openclawAgentId,
    agent?.chatAppCharacterId,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)));
}

function findSyncedCompletion(agentIds, activeTask, minCompletedAt, options = {}) {
  return findRecentPlatformSyncedCompletion({
    tasks: useWorldStore.getState().completedTasks,
    agentIds,
    activeTask,
    minCompletedAt,
    ...options,
  });
}

async function waitForSyncedCompletion(agentIds, activeTask, minCompletedAt) {
  const deadline = Date.now() + 1500;
  let candidate = findSyncedCompletion(agentIds, activeTask, minCompletedAt, {
    requireArtifacts: true,
  });
  while (!candidate && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    candidate = findSyncedCompletion(agentIds, activeTask, minCompletedAt, {
      requireArtifacts: true,
    });
  }
  return candidate;
}

export async function executeTask(agentId, taskMessage) {
  const agent = useWorldStore.getState().agents.find((a) => a.id === agentId);
  if (!agent) throw new Error('Agent not found');

  const taskToolUse = buildOpenClawTaskToolUse();
  useWorldStore.getState().setAgentToolUse(agentId, taskToolUse);

  let acc = '';
  let taskArtifacts = [];
  // Read active task from fresh state (after startNextTask has been called)
  const activeTask = useWorldStore.getState().activeTasks[agentId] || null;
  const activeTaskId = activeTask?.id;
  const activeTaskLabel = activeTask?.label;
  const activeTaskStartedAt = activeTask?.startedAt || Date.now();
  const taskAgentIds = getTaskAgentIds(agentId, agent, activeTask);

  await syncOpenClawTaskStart(agentId, activeTask, taskToolUse);

  try {
    const taskResult = await runAgentTask(agentId, taskMessage, {
      taskId: activeTaskId,
    });
    acc = typeof taskResult?.text === 'string' ? taskResult.text : '';
    taskArtifacts = Array.isArray(taskResult?.artifacts) ? taskResult.artifacts : [];
  } catch (error) {
    const message = error?.message || String(error);
    const errorDetails = getBackendErrorDetail(error, message);
    const snapshot = await syncOpenClawTaskEnd(
      agentId,
      buildOpenClawTaskEndPayload({
        taskId: activeTaskId,
        error: message,
        errorDetails,
      })
    );

    if (!snapshot) {
      useWorldStore.getState().failTask(agentId, message, errorDetails);
      useWorldStore.getState().clearAgentToolUse(agentId);
    }

    const failed =
      findTaskInSnapshot(snapshot, activeTaskId) ||
      findTaskByIdentity(useWorldStore.getState().completedTasks, {
        agentId,
        id: activeTaskId,
      });
    if (failed) {
      useWorldStore.getState().openResultModal(failed);
    }
    throw error;
  }

  const syncedCompletion = taskArtifacts.length > 0
    ? null
    : await waitForSyncedCompletion(
      taskAgentIds,
      activeTask,
      activeTaskStartedAt - 1000
    );
  const syncedArtifacts = Array.isArray(syncedCompletion?.artifacts)
    ? syncedCompletion.artifacts
    : [];
  const artifacts = taskArtifacts.length > 0 ? taskArtifacts : syncedArtifacts;
  const syncedResult =
    typeof syncedCompletion?.result === 'string' && syncedCompletion.result.trim()
      ? syncedCompletion.result
      : acc;

  const snapshot = await syncOpenClawTaskEnd(
    agentId,
    buildOpenClawTaskEndPayload({
      taskId: activeTaskId,
      result: syncedResult,
      artifacts,
    })
  );

  if (!snapshot) {
    useWorldStore.getState().completeTask(agentId, acc);
    useWorldStore.getState().clearAgentToolUse(agentId);
  }

  const completedTask =
    findTaskInSnapshot(snapshot, activeTaskId) ||
    syncedCompletion ||
    findTaskByIdentity(useWorldStore.getState().completedTasks, {
      agentId,
      id: activeTaskId,
    });
  if (completedTask) {
    useWorldStore.getState().openResultModal(completedTask);
  }

  const taskEntry = completedTask || { label: activeTaskLabel };
  saveMemory(
    agentId,
    'task_complete',
    `${taskEntry?.label || 'Task'}: ${acc.slice(0, 120)}`,
    ['task', taskEntry?.label?.split(' ')[0]].filter(Boolean)
  );

  return acc;
}
